import { useState, useEffect } from 'react'
import { getAnalytics, resetAnalytics, type Analytics } from '../services/api'

export default function AnalyticsPage() {
  const [analytics, setAnalytics] = useState<Analytics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [autoRefresh, setAutoRefresh] = useState(true)

  const fetchAnalytics = async () => {
    try {
      const data = await getAnalytics()
      setAnalytics(data)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取分析数据失败')
    } finally {
      setLoading(false)
    }
  }

  const handleReset = async () => {
    if (!confirm('确定要重置所有分析数据吗？此操作不可恢复。')) {
      return
    }

    try {
      await resetAnalytics()
      await fetchAnalytics()
    } catch (err) {
      setError(err instanceof Error ? err.message : '重置失败')
    }
  }

  useEffect(() => {
    fetchAnalytics()
  }, [])

  useEffect(() => {
    if (!autoRefresh) return

    const interval = setInterval(fetchAnalytics, 5000) // 每5秒刷新
    return () => clearInterval(interval)
  }, [autoRefresh])

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="text-center py-12">
          <div className="inline-block w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-4 text-gray-600">加载中...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="card bg-red-50 border-red-200">
          <p className="text-red-800">{error}</p>
          <button onClick={fetchAnalytics} className="btn btn-secondary mt-4">
            重试
          </button>
        </div>
      </div>
    )
  }

  if (!analytics) {
    return null
  }

  const successRate = analytics.totalRequests > 0
    ? ((analytics.successfulRequests / analytics.totalRequests) * 100).toFixed(1)
    : '0.0'

  const failureRate = analytics.totalRequests > 0
    ? ((analytics.failedRequests / analytics.totalRequests) * 100).toFixed(1)
    : '0.0'

  const uptime = analytics.startTime
    ? Math.floor((Date.now() - new Date(analytics.startTime).getTime()) / 1000 / 60)
    : 0

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* 标题栏 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">后端监控仪表板</h1>
          <p className="text-gray-600 mt-2">
            实时查看服务器请求状态、API使用情况和性能指标
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            自动刷新
          </label>
          <button onClick={fetchAnalytics} className="btn btn-secondary">
            🔄 刷新
          </button>
          <button onClick={handleReset} className="btn btn-secondary text-red-600 hover:bg-red-50">
            🗑️ 重置数据
          </button>
        </div>
      </div>

      {/* 运行时间提示 */}
      <div className="card bg-yellow-50 border-yellow-200">
        <p className="text-sm text-yellow-800">
          ⚠️ 注意：数据存储在 Worker 内存中，Worker 重启后会自动重置。当前运行时长: <strong>{uptime}</strong> 分钟
        </p>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* 总请求数 */}
        <div className="card bg-blue-50 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">总请求数</p>
              <p className="text-3xl font-bold text-blue-600 mt-2">{analytics.totalRequests}</p>
            </div>
            <div className="text-4xl">📊</div>
          </div>
        </div>

        {/* 成功请求 */}
        <div className="card bg-green-50 border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">成功请求</p>
              <p className="text-3xl font-bold text-green-600 mt-2">{analytics.successfulRequests}</p>
              <p className="text-xs text-green-700 mt-1">成功率: {successRate}%</p>
            </div>
            <div className="text-4xl">✅</div>
          </div>
        </div>

        {/* 失败请求 */}
        <div className="card bg-red-50 border-red-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">失败请求</p>
              <p className="text-3xl font-bold text-red-600 mt-2">{analytics.failedRequests}</p>
              <p className="text-xs text-red-700 mt-1">失败率: {failureRate}%</p>
            </div>
            <div className="text-4xl">❌</div>
          </div>
        </div>

        {/* 运行时长 */}
        <div className="card bg-purple-50 border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">运行时长</p>
              <p className="text-3xl font-bold text-purple-600 mt-2">{uptime}</p>
              <p className="text-xs text-purple-700 mt-1">分钟</p>
            </div>
            <div className="text-4xl">⏱️</div>
          </div>
        </div>
      </div>

      {/* API 端点统计 */}
      <div className="card">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">API 端点调用统计</h2>
        {Object.keys(analytics.endpointStats).length > 0 ? (
          <div className="space-y-3">
            {Object.entries(analytics.endpointStats)
              .sort(([, a], [, b]) => b - a)
              .map(([endpoint, count]) => {
                const percentage = analytics.totalRequests > 0
                  ? ((count / analytics.totalRequests) * 100).toFixed(1)
                  : '0.0'
                return (
                  <div key={endpoint} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-mono text-gray-700">{endpoint}</span>
                      <span className="font-semibold text-gray-900">{count} 次 ({percentage}%)</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-primary-600 h-2 rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                )
              })}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">暂无数据</p>
        )}
      </div>

      {/* AI 模型使用统计 */}
      <div className="card">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">AI 模型使用统计</h2>
        {Object.keys(analytics.modelStats).length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(analytics.modelStats)
              .sort(([, a], [, b]) => b - a)
              .map(([model, count]) => (
                <div key={model} className="card bg-gradient-to-br from-blue-50 to-purple-50 border-blue-200">
                  <p className="text-sm font-medium text-gray-700">{model}</p>
                  <p className="text-2xl font-bold text-primary-600 mt-2">{count}</p>
                  <p className="text-xs text-gray-600 mt-1">次调用</p>
                </div>
              ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">暂无数据</p>
        )}
      </div>

      {/* 最近请求日志 */}
      <div className="card">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">最近请求日志（最新 20 条）</h2>
        {analytics.recentLogs.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-3 font-semibold text-gray-700">时间</th>
                  <th className="text-left py-2 px-3 font-semibold text-gray-700">方法</th>
                  <th className="text-left py-2 px-3 font-semibold text-gray-700">路径</th>
                  <th className="text-left py-2 px-3 font-semibold text-gray-700">状态</th>
                  <th className="text-left py-2 px-3 font-semibold text-gray-700">耗时</th>
                  <th className="text-left py-2 px-3 font-semibold text-gray-700">模型</th>
                </tr>
              </thead>
              <tbody>
                {analytics.recentLogs.map((log, index) => {
                  const statusColor =
                    log.status >= 200 && log.status < 300
                      ? 'text-green-600 bg-green-50'
                      : log.status >= 400
                      ? 'text-red-600 bg-red-50'
                      : 'text-yellow-600 bg-yellow-50'

                  const durationColor =
                    log.duration < 1000
                      ? 'text-green-600'
                      : log.duration < 5000
                      ? 'text-yellow-600'
                      : 'text-red-600'

                  return (
                    <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-2 px-3 text-gray-600 font-mono text-xs">
                        {new Date(log.timestamp).toLocaleTimeString('zh-CN')}
                      </td>
                      <td className="py-2 px-3">
                        <span className="px-2 py-1 rounded bg-gray-100 font-mono text-xs">
                          {log.method}
                        </span>
                      </td>
                      <td className="py-2 px-3 font-mono text-xs text-gray-700">{log.path}</td>
                      <td className="py-2 px-3">
                        <span className={`px-2 py-1 rounded font-semibold text-xs ${statusColor}`}>
                          {log.status}
                        </span>
                      </td>
                      <td className={`py-2 px-3 font-mono text-xs ${durationColor}`}>
                        {log.duration}ms
                      </td>
                      <td className="py-2 px-3 text-gray-600 text-xs">
                        {log.model || '-'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">暂无日志</p>
        )}
      </div>
    </div>
  )
}
